import React, { useEffect } from "react";
import { Button, Input, Pagination, SelectMenu, Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import TripPublicationTable from "./components/TripPublicationTable";
import { useManageTripPublication } from "./hooks/useManageTripPublication";

import styles from "./TripPublication.module.css";

const statusOptions = [
  { value: "all", label: "Tous" },
  { value: "DRAFTS", label: "Brouillons" },
  { value: "PUBLISHED", label: "Publiés" },
  { value: "ARCHIVED", label: "Archivés" },
];

const TripPublication = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    trips,
    tripsLoading,
    tripsRefreshing,
    error,
    currentPage,
    setCurrentPage,
    nbPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedEstablishment,
    setSelectedEstablishment,
    establishmentFilterOptions,
  } = useManageTripPublication();

  useEffect(() => {
    if (error) {
      navigate("/error");
    }
  }, [error, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.headerCard}>
        <div>
          <Text as="h3" variant="heading" size="lg" weight="semibold">
            {t("tripPublication.trips")}
          </Text>
          <Text size="sm" color="subtle">
            Administration des publications de circuits
          </Text>
        </div>

        <Button
          label={t("tripPublication.actions.addTrip")}
          variant="primary"
          iconName="iconPlus"
          iconPosition="left"
          onClick={() => navigate("add")}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.filterBar}>
          <div className={styles.searchWrapper}>
            <Input
              inputSize="small"
              iconLeft="iconSearch"
              placeholder="Rechercher..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className={styles.filtersRight}>
            <SelectMenu t={t}
              inputSize="small"
              options={establishmentFilterOptions}
              value={selectedEstablishment}
              onChange={(value, option) => setSelectedEstablishment(option.value)}
            />

            <div className={styles.statusToggle}>
              {statusOptions.map((option) => (
                <Button
                  key={option.value}
                  size="small"
                  variant={statusFilter === option.value ? "primary" : "secondary"}
                  label={option.label}
                  onClick={() => setStatusFilter(option.value)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Pas de Preloader plein écran ici : il recouvrirait la page à chaque
            filtre. Premier chargement : indicateur inline ; ensuite la table
            reste affichée, grisée pendant la requête. */}
        {tripsLoading ? (
          <div className={styles.loading} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <Text size="sm" color="muted">
              Chargement des circuits…
            </Text>
          </div>
        ) : (
          <div
            className={tripsRefreshing ? styles.refreshing : undefined}
            aria-busy={tripsRefreshing}
          >
            <TripPublicationTable
              trips={trips}
              onOpenTrip={(publicationId) => navigate(`${publicationId}`)}
              onEditTrip={(publicationId, tripId) => navigate(`${publicationId}/${tripId}/edit`)}
              onPublishTrip={(publicationId) => navigate(`${publicationId}/publish`)}
            />
          </div>
        )}

        <Pagination
          totalPages={nbPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default TripPublication;
